import type { ComponentType, ChangeEvent } from 'react';
import { Controller } from 'react-hook-form';
import type { Control, FieldErrors, UseFormSetValue } from 'react-hook-form';
import { DELIVERABLE_REGISTRY, isOfferedOnPlatform, hasReelDeliverable } from '@discovr/types';
import { Check, Info, LockSimple, Image as ImageIcon, type IconWeight } from '../design-system/icons';
import { SimpleTooltip } from '../components/ui/tooltip';
import {
  YoutubeShortsIcon,
  YoutubePlayIcon,
  InstagramStoryIcon,
  InstagramReelIcon,
  InstagramPostIcon,
  InstagramCarouselIcon,
  InstagramCollabReelIcon,
} from '../components/icons';
import type { CampaignFormData } from './campaignSchema';
import { err, USAGE_RIGHTS_MIN_DAYS, USAGE_RIGHTS_MAX_DAYS } from './constants';
import { CampaignAudioSection } from './CampaignAudioSection';

// Broader than Phosphor's `Icon` (a ForwardRefExoticComponent) so this slot
// also accepts our hand-authored custom glyphs (plain function components)
// replicated verbatim from Figma's bespoke icon set — see each icon file's
// doc comment for its source node.
type Glyph = ComponentType<{ size?: number; weight?: IconWeight; className?: string }>;

// Instagram brand gradient (brand mark, NOT the Discovr AI gradient) — Figma.
// This section now serves both platforms; IG keeps its gradient as an approved exception.
const IG_GRADIENT = 'linear-gradient(135deg, #FEDA75 0%, #FA7E1E 25%, #D62976 50%, #962FBF 75%, #4F5BD5 100%)';

// Icon-box treatment per platform. IG keeps its brand gradient; YouTube uses brand red.
// Final values verified against Figma content-type card nodes (1173-15172 / 1173-15081).
const ICON_BOX_BG: Record<string, string> = {
  instagram: IG_GRADIENT,
  youtube: '#FF0000',
  x: '#000000',
};

// Per-type glyph + sublabel + optional label override for the Figma content-type
// cards. Registry is zero-dependency (no React), so the icon/sublabel mapping
// lives here. Icons verified against Figma nodes 3072-66911 (IG) / 3072-67007
// (YT) — every glyph below is a bespoke Figma icon (not a Phosphor lookalike),
// replicated in packages/ui/src/components/icons. Integrated/Dedicated share
// the same Figma component (58:795, "Type=Default") — a fixed white-badge/
// red-triangle glyph, not the tile-colored treatment the other icons use.
const TYPE_META: Record<string, { Icon: Glyph; sub: string; label?: string }> = {
  instagram_stories: { Icon: InstagramStoryIcon, sub: 'Instagram · 24h' },
  instagram_static_post: { Icon: InstagramPostIcon, sub: 'Instagram · Feed' },
  instagram_carousel: { Icon: InstagramCarouselIcon, sub: 'Instagram · Multi' },
  instagram_non_collab_reel: { Icon: InstagramReelIcon, sub: 'Instagram · Video' },
  instagram_collab_reel: { Icon: InstagramCollabReelIcon, sub: 'Instagram · 2 creators' },
  youtube_integrated: { Icon: YoutubePlayIcon, label: 'Integrated', sub: 'YouTube · Sponsors' },
  youtube_dedicated: { Icon: YoutubePlayIcon, label: 'Dedicated', sub: 'YouTube · Long-form' },
  youtube_shorts: { Icon: YoutubeShortsIcon, label: 'Shorts', sub: 'YouTube · Vertical' },
};

// Card display order per platform, to match Figma. Registry order untouched.
const MAIN_ORDER: Record<string, string[]> = {
  instagram: [
    'instagram_stories',
    'instagram_non_collab_reel',
    'instagram_static_post',
    'instagram_carousel',
    'instagram_collab_reel',
  ],
  youtube: ['youtube_integrated', 'youtube_dedicated', 'youtube_shorts'],
};
const mainRank = (platform: string, type: string) => {
  const order = MAIN_ORDER[platform] ?? [];
  const i = order.indexOf(type);
  return i === -1 ? order.length : i;
};

// Figma content-type card: gradient icon box + title + sublabel; selected = black
// border + purple check (matches the Step 1 Choice Card pattern).
function TypeCard({
  type,
  label,
  platform,
  selected,
  onClick,
}: {
  type: string;
  label: string;
  platform: string;
  selected: boolean;
  onClick: () => void;
}) {
  const meta = TYPE_META[type];
  const Glyph = meta?.Icon ?? ImageIcon;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative flex cursor-pointer items-center gap-3 rounded-xl border-[1.5px] p-3 text-left transition-colors ${selected ? 'border-foreground bg-white' : 'border-border bg-card hover:border-foreground/30'}`}
    >
      <span
        className="flex size-10 shrink-0 items-center justify-center rounded-[10px] text-white"
        style={{ background: ICON_BOX_BG[platform] ?? 'var(--muted)' }}
      >
        <Glyph size={20} weight="regular" />
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-body-sm-medium text-foreground">{meta?.label ?? label}</span>
        <span className="truncate text-caption-regular text-muted-foreground">{meta?.sub ?? platform}</span>
      </span>
      {selected && (
        <span className="absolute top-2.5 right-2.5 flex size-4 items-center justify-center rounded-full bg-(--brand-500) text-white">
          <Check size={11} weight="bold" />
        </span>
      )}
    </button>
  );
}

interface DeliverablesSectionProps {
  control: Control<CampaignFormData>;
  setValue: UseFormSetValue<CampaignFormData>;
  errors: FieldErrors<CampaignFormData>;
  platform: CampaignFormData['platform'];
  watchDeliverables: string[];
  watchUsageRights: CampaignFormData['usageRights'];
}

function TypePill({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex select-none items-center gap-1.5 rounded-full border px-3 py-[9px] text-caption-medium transition-all duration-100 ${
        selected
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-card text-muted-foreground hover:border-border'
      }`}
    >
      {label}
      {selected && (
        <span className="flex size-3.5 shrink-0 items-center justify-center rounded-full border border-white/40 text-[8px]">
          ✓
        </span>
      )}
    </button>
  );
}

const DURATION_INPUT_BASE =
  'h-8 w-20 rounded-lg border text-center text-body-sm-regular text-foreground outline-none transition-all focus:ring-2';
const DURATION_INPUT_INVALID =
  'border-border-destructive-default focus:border-border-destructive-default focus:ring-border-destructive-default/15';
const DURATION_INPUT_VALID = 'border-border focus:border-foreground focus:ring-ring/10';

function getDurationInputClasses(hasError: boolean): string {
  return `${DURATION_INPUT_BASE} ${hasError ? DURATION_INPUT_INVALID : DURATION_INPUT_VALID}`;
}

export function DeliverablesSection({
  control,
  setValue,
  errors,
  platform,
  watchDeliverables,
}: DeliverablesSectionProps) {
  const selected = new Set(watchDeliverables ?? []);

  const toggle = (type: string) => {
    const next = selected.has(type)
      ? (watchDeliverables ?? []).filter((t) => t !== type)
      : [...(watchDeliverables ?? []), type];
    setValue('deliverables', next, { shouldValidate: true });
  };

  const mainTypes = Object.entries(DELIVERABLE_REGISTRY)
    .filter(([, meta]) => meta.platform === platform && meta.category === 'main')
    .sort(([a], [b]) => mainRank(platform, a) - mainRank(platform, b));
  const addonTypes = Object.entries(DELIVERABLE_REGISTRY).filter(
    ([, meta]) => meta.platform === platform && meta.category === 'addon',
  );

  return (
    <div className="space-y-6">
      {/* Main deliverable types — Figma content-type cards */}
      <div>
        <div className={`grid gap-3 ${platform === 'instagram' ? 'grid-cols-4' : 'grid-cols-3'}`}>
          {mainTypes.map(([type, meta]) => (
            <TypeCard
              key={type}
              type={type}
              label={meta.label}
              platform={platform}
              selected={selected.has(type)}
              onClick={() => toggle(type)}
            />
          ))}
        </div>
        {errors.deliverables && (
          <p className={`${err} mt-1.5`}>
            {String((errors.deliverables as { message?: string })?.message ?? '')}
          </p>
        )}
      </div>

      {addonTypes.length > 0 && (
        <>
          <hr className="border-border" />
          <div>
            <p className="mb-3 text-[10px] font-medium uppercase tracking-[0.6px] text-muted-foreground">
              Add-ons · optional boosters
            </p>
            <div className="flex flex-wrap gap-1.5">
              {addonTypes.map(([type, meta]) => (
                <TypePill
                  key={type}
                  label={meta.label}
                  selected={selected.has(type)}
                  onClick={() => toggle(type)}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {/* usage_rights platform policy — single predicate shared with the BE
          (OfferedOnPlatform); widening the '*' case there + in the registry
          opens this toggle for new platforms with no change here. */}
      {isOfferedOnPlatform('usage_rights', platform) && (
      <>
      <hr className="border-border" />

      {/* Usage rights */}
      <div className="mt-2">
        <div className="mb-2 flex items-center gap-1.5">
          <LockSimple size={14} className="shrink-0 text-muted-foreground" />
          <p className="text-[10px] font-medium uppercase text-muted-foreground">
            Usage rights
          </p>
        </div>
        <Controller
          name="usageRights"
          control={control}
          render={({ field }) => {
            const isOn = field.value != null;
            const handleDurationChange = (e: ChangeEvent<HTMLInputElement>) =>
              field.onChange({ days: e.target.valueAsNumber });
            return (
              <div className="rounded-xl border border-border transition-all duration-150">
                <div
                  className="flex cursor-pointer items-center justify-between px-[18px] py-3.5"
                  onClick={() => field.onChange(isOn ? null : { days: 30 })}
                >
                  <div className="flex flex-col gap-0.5">
                    <span className="inline-flex items-center gap-1.5 text-body-sm-medium text-foreground">
                      Include usage rights
                      <SimpleTooltip content="Grants rights to reuse this content for your paid ad campaigns.">
                        <button
                          type="button"
                          aria-label="About usage rights"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex shrink-0 cursor-help text-muted-foreground"
                        >
                          <Info size={14} />
                        </button>
                      </SimpleTooltip>
                    </span>
                    <span className="text-caption-regular text-muted-foreground">
                      Rights to repurpose creator content
                    </span>
                  </div>
                  <div
                    className={`relative h-5 w-9 shrink-0 rounded-full transition-colors duration-150 ${
                      isOn ? 'bg-background-primary-default' : 'bg-muted'
                    }`}
                  >
                    <div
                      className={`absolute top-[3px] size-3.5 rounded-full bg-card transition-all duration-150 ${
                        isOn ? 'right-[3px]' : 'left-[3px]'
                      }`}
                    />
                  </div>
                </div>
                {isOn && (
                  <div className="border-t border-border px-3.5 pb-3.5 pt-3">
                    <div className="flex items-center gap-2.5">
                      <span className="whitespace-nowrap text-caption-medium text-muted-foreground">
                        Duration
                      </span>
                      <input
                        type="number"
                        min={USAGE_RIGHTS_MIN_DAYS}
                        max={USAGE_RIGHTS_MAX_DAYS}
                        className={getDurationInputClasses(Boolean(errors.usageRights?.days))}
                        // Empty box (NaN) is allowed while typing; validated on Continue.
                        value={Number.isFinite(field.value?.days) ? field.value?.days : ''}
                        onChange={handleDurationChange}
                      />
                      <span className="whitespace-nowrap text-[10px] text-muted-foreground">
                        days (min {USAGE_RIGHTS_MIN_DAYS} · max 3 yrs / {USAGE_RIGHTS_MAX_DAYS})
                      </span>
                    </div>
                    {errors.usageRights?.days && (
                      <p className={`${err} mt-1.5`}>{errors.usageRights.days.message}</p>
                    )}
                  </div>
                )}
              </div>
            );
          }}
        />
      </div>
      </>
      )}

      {/* Campaign audio — Official Audio Compliance (Instagram Reels only: the
          BE can only scrape a published Reel's audio, so the step stays
          hidden until a Reel deliverable is actually selected — mirrors the
          BE's audioComplianceReelLookup gate). The verified URL + resolved
          track ship as part of the same create-campaign body
          (campaignSchema.ts's buildCampaignPayload). NewCampaign.tsx clears
          any already-entered value if the brand deselects the last Reel
          after this section was visible. */}
      {platform === 'instagram' && hasReelDeliverable([...selected]) && (
      <>
      <hr className="border-border" />
      <Controller
        name="officialAudioUrl"
        control={control}
        render={({ field }) => (
          <CampaignAudioSection
            value={field.value ?? ''}
            onChange={field.onChange}
            // Lift the resolved song/artist into the form so submit can include it
            // in the create payload — the backend can't reliably re-resolve (IG consent wall).
            onResolved={(track) => setValue('officialAudioTrack', track)}
          />
        )}
      />
      </>
      )}
    </div>
  );
}
